import { useState } from 'react';

/**
 * Per-view "filter by person", multi-select — the individual-engineer analogue
 * of {@link useTeamFilter}. Options are the given engineers; an empty selection
 * means "All people". The control hides itself when there are fewer than two
 * engineers (nothing to narrow). Composes with the team filter — both narrow
 * the same engineer set (intersection).
 */
export interface PersonFilter {
  selected: string[];
  setSelected: (value: string[]) => void;
  options: Array<{ value: string; label: string }>;
  /** Whether to render the control. */
  show: boolean;
  /** Narrow engineers to the selected people (no-op for an empty selection). */
  filter: <E extends { id: string }>(engineers: E[]) => E[];
}

/** Pure: narrow engineers to the selected ids (empty selection = all). */
export function filterEngineersByPerson<E extends { id: string }>(
  engineers: E[],
  selected: string[],
): E[] {
  if (selected.length === 0) return engineers;
  const wanted = new Set(selected);
  return engineers.filter((e) => wanted.has(e.id));
}

export function usePersonFilter(
  engineers: ReadonlyArray<{ id: string; display_name: string }>,
): PersonFilter {
  const [selected, setSelected] = useState<string[]>([]);
  const options = engineers
    .map((e) => ({ value: e.id, label: e.display_name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return {
    selected,
    setSelected,
    options,
    show: engineers.length > 1,
    filter: (list) => filterEngineersByPerson(list, selected),
  };
}
